import { useMemo, useState } from 'react';
import objectPath from 'object-path';
import {
  DashboardOnboardingTours,
  Header,
  HeaderMobile,
  NavConfigProvider,
  NavVertical,
} from 'src/layouts/dashboard';
import { ImpersonationBanner } from 'src/layouts/dashboard/header/ImpersonationBanner';

import { IMPERSONATION_BANNER_HEIGHT, useImpersonationSessions } from '@hooks/impersonation';
import { Box, Toolbar, useMediaQuery } from '@mui/material';

import { useHtmlClassService } from '../_core/MetronicLayout';

import { ScrollTop } from './extras/ScrollTop';
import { SubHeader } from './subheader/SubHeader';
import { LayoutInit } from './LayoutInit';

const PAGE_PADDING_X = { xs: '15px', lg: '25px' };
const PAGE_PADDING_Y = { xs: '15px', lg: '25px' };

export function Layout({ children }) {
  const uiService = useHtmlClassService();
  const layoutProps = useMemo(
    () => ({
      selfLayout: objectPath.get(uiService.config, 'self.layout'),
      asideDisplay: objectPath.get(uiService.config, 'aside.self.display'),
      subheaderDisplay: objectPath.get(uiService.config, 'subheader.display'),
      contentExtended: objectPath.get(uiService.config, 'content.extended'),
    }),
    [uiService]
  );

  const isMd = useMediaQuery((theme) => theme.breakpoints.up('md'));
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const { isImpersonating } = useImpersonationSessions();

  if (layoutProps.selfLayout === 'blank') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', flex: 1 }}>
        {children}
      </Box>
    );
  }

  return (
    <NavConfigProvider>
      {isImpersonating && <ImpersonationBanner />}
      {!isMd && <HeaderMobile onOpenSidebar={() => setMobileSidebarOpen(true)} />}
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', flex: 1 }}>
        <Box sx={{ display: 'flex', flex: '1 0 auto' }}>
          {layoutProps.asideDisplay && (
            <NavVertical
              mobileOpen={mobileSidebarOpen}
              onMobileClose={() => setMobileSidebarOpen(false)}
            />
          )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: '1 1 auto',
              minWidth: 0,
              // What a `position: sticky` element inside a page has to clear: the fixed
              // header, and the impersonation banner when it pushes that header down.
              '--app-toolbar-height': { xs: '56px', sm: '64px' },
              '--app-header-offset': isImpersonating
                ? `calc(var(--app-toolbar-height) + ${IMPERSONATION_BANNER_HEIGHT}px)`
                : 'var(--app-toolbar-height)',
            }}
          >
            {isMd && <Header />}
            {isImpersonating && <Box sx={{ height: `${IMPERSONATION_BANNER_HEIGHT}px` }} />}
            <Toolbar />
            {layoutProps.subheaderDisplay && <SubHeader />}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: '1 0 auto',
                py: PAGE_PADDING_Y,
              }}
            >
              {layoutProps.contentExtended ? (
                children
              ) : (
                <Box sx={{ display: 'flex', flex: '1 0 auto' }}>
                  <Box sx={{ width: '100%', px: PAGE_PADDING_X }}>{children}</Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      <ScrollTop />
      <DashboardOnboardingTours />
      <LayoutInit />
    </NavConfigProvider>
  );
}
